import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";

type Providers = { credentials: boolean; google: boolean; apple: boolean };

function safeNext(value: string | null) {
  return value?.startsWith("/") && !value.startsWith("//") ? value : null;
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285f4" d="M21.35 12.27c0-.71-.06-1.24-.2-1.79H12v3.26h5.37c-.11.81-.69 2.03-1.99 2.85l-.02.11 2.89 2.24.2.02c1.84-1.7 2.9-4.2 2.9-6.69Z" />
      <path fill="#34a853" d="M12 21.8c2.63 0 4.84-.87 6.45-2.37l-3.07-2.37c-.82.55-1.93.94-3.38.94-2.58 0-4.77-1.7-5.55-4.05l-.11.01-3 2.32-.04.11A9.74 9.74 0 0 0 12 21.8Z" />
      <path fill="#fbbc05" d="M6.45 13.95A5.85 5.85 0 0 1 6.14 12c0-.68.12-1.34.3-1.95v-.12L3.4 7.57l-.1.05A9.82 9.82 0 0 0 2.25 12c0 1.58.38 3.07 1.05 4.38l3.15-2.43Z" />
      <path fill="#eb4335" d="M12 6c1.83 0 3.06.79 3.76 1.44l2.75-2.68C16.82 3.18 14.63 2.2 12 2.2a9.74 9.74 0 0 0-8.7 5.42l3.14 2.43C7.23 7.7 9.42 6 12 6Z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.32.03-1.75-.79-3.27-.79-1.53 0-2 .77-3.25.82-1.3.05-2.29-1.32-2.96-2.54-3.4-4.35-3.78-9.42-1.58-12.05 1.1-1.31 2.8-2.14 4.4-2.16 1.37-.03 2.65.93 3.27.93.59 0 2.3-1.15 3.88-.98.66.03 2.52.27 3.71 2.01-.1.06-2.22 1.28-2.2 3.9.03 3.13 2.74 4.17 2.77 4.18-.03.07-.43 1.45-1.43 2.92l-.29.29ZM13.5 3.63c.7-.83 1.87-1.47 2.84-1.51.13 1.17-.34 2.35-1 3.19-.65.83-1.73 1.47-2.8 1.38-.15-1.15.38-2.35.96-3.06Z" />
    </svg>
  );
}

export function LoginPage() {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const next = safeNext(query.get("next"));
  const error = query.get("error");
  const [providers, setProviders] = useState<Providers>({ credentials: true, google: false, apple: false });
  const [providersLoaded, setProvidersLoaded] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "verbcoach login";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    fetch("/auth/providers", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((value: Providers) => setProviders(value))
      .catch(() => undefined)
      .finally(() => setProvidersLoaded(true));
  }, []);

  return (
    <main className="login-page">
      <header className="login-page__header">
        <a href="/about" aria-label="VerbCoach home" className="login-page__brand">
          <BrandLogo alt="VerbCoach" />
        </a>
        <a href="/about" className="login-page__back"><ArrowLeft size={17} aria-hidden="true" /> Back</a>
      </header>

      <section className="login-page__surface" aria-labelledby="login-title">
        <div className="login-page__intro">
          <span className="login-page__eyebrow"><ShieldCheck size={15} aria-hidden="true" /> Secure access</span>
          <h1 id="login-title">Welcome back</h1>
          <p>Use your VerbCoach credentials or a verified identity provider.</p>
        </div>

        {error && (
          <div className="login-page__error" role="alert">
            {error === "provider"
              ? "The identity provider could not complete sign-in. Please try again."
              : "Email, username or password not recognised. Check your details or wait before trying again."}
          </div>
        )}

        <div className="login-providers">
          {providers.google ? (
            <a href={`/auth/google/login${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="login-provider">
              <span className="login-provider__mark"><GoogleIcon /></span>
              <span><span className="login-provider__prefix">Sign in with </span>Google</span>
            </a>
          ) : (
            <button className="login-provider" type="button" disabled aria-label="Google sign-in is not configured">
              <span className="login-provider__mark"><GoogleIcon /></span>
              <span><span className="login-provider__prefix">Sign in with </span>Google</span>
            </button>
          )}
          {providers.apple ? (
            <a href={`/auth/apple/login?next=${encodeURIComponent(next)}`} className="login-provider login-provider--apple">
              <span className="login-provider__mark"><AppleIcon /></span>
              <span><span className="login-provider__prefix">Sign in with </span>Apple</span>
            </a>
          ) : (
            <button className="login-provider login-provider--apple" type="button" disabled aria-label="Apple sign-in is not configured">
              <span className="login-provider__mark"><AppleIcon /></span>
              <span><span className="login-provider__prefix">Sign in with </span>Apple</span>
            </button>
          )}
        </div>

        {providersLoaded && (!providers.google || !providers.apple) && (
          <p className="login-providers__status" role="status">Social sign-in is awaiting administrator setup.</p>
        )}

        <div className="login-page__separator"><span>or use your credentials</span></div>

        <section className="login-coming-soon" aria-label="Email and password login" aria-disabled="true">
          <span className="login-coming-soon__label">Coming soon</span>
          <p>Email and password sign-in is not available yet.</p>
        </section>

        <p className="login-page__note">Access attempts are protected and logged.</p>
      </section>
    </main>
  );
}
